// Browser-only voice recording helpers (PRD.279). Kept apart from
// `vtt-support.ts` (the DOM-free, unit-testable transcription client) because
// these touch MediaRecorder / navigator / FileReader and cannot run under
// node:test. Shared by useVoiceToText and the Elia dictation hook.

/**
 * Pick a MIME type this browser can actually record. Prefer Opus-in-WebM
 * (Chrome/Firefox/Android), fall back to MP4/AAC (Safari/iOS), then bare webm.
 * Returns null when the browser supports none (caller shows an error).
 */
export function pickSupportedMimeType(): string | null {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/aac',
    'audio/ogg;codecs=opus',
  ];
  const isSupported = (t: string) =>
    typeof MediaRecorder !== 'undefined' &&
    typeof MediaRecorder.isTypeSupported === 'function' &&
    MediaRecorder.isTypeSupported(t);
  for (const t of candidates) {
    if (isSupported(t)) return t;
  }
  // Older Safari lacks isTypeSupported but can still record its default.
  if (typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported !== 'function') {
    return 'audio/mp4';
  }
  return null;
}

/** True when this browser can record audio at all (used to gate the mic). */
export function isRecordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    pickSupportedMimeType() !== null
  );
}

/** Read a Blob as base64 (no data-URL prefix). Browser-only (FileReader). */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1]);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
